import {
  ReportBuilderDataSourceId,
  TemplateScope,
} from '@principle-theorem/principle-core/interfaces';
import { FactTables } from '../../models/fact-tables';
import { toMeasureBuilderData } from '../../models/measure-properties';
import { ReportBuilderColumns } from '../../report-builder-columns';
import { ChartFilterDefinition } from '../charts/chart-filter-definition';
import { ReportBuilderCharts } from '../charts/report-builder-charts';
import { ReportBuilderCommonCharts } from '../charts/report-builder-common-charts';
import { ReportBuilderDataSourceInstance } from '../report-builder-data-source-instance';
import { ReportBuilderGroups } from '../report-builder-groups';
import { ReportBuilderScopeDataAccessors } from '../report-builder-scope-data-accessors';

export class TransactionsDataSource extends ReportBuilderDataSourceInstance {
  override readonly id = ReportBuilderDataSourceId.Transactions;
  override readonly name = 'Transactions';
  override readonly description =
    'One result per transaction within Principle. This includes information about the practice, patient, and invoice associated with each transaction.';
  override readonly factTable = FactTables.transactionEvent.latestEvent;
  override readonly rowItemName = 'Transaction';

  override readonly queryableTimestamps = [
    { property: this.factTable.transaction.createdAt },
    { property: this.factTable.invoice.createdAt },
    { property: this.factTable.invoice.issuedAt },
    { property: this.factTable.invoice.paidAt },
  ];

  scopes = [TemplateScope.Patient];
  scopeDataAccessor = ReportBuilderScopeDataAccessors.build([
    this.factTable.patient,
  ]);

  override readonly groupByOptions = [
    ReportBuilderGroups.toGroup('Common', [
      ReportBuilderGroups.toOption(this.factTable.transaction.createdAt),
      ReportBuilderGroups.toOption(this.factTable.practice.name),
      ReportBuilderGroups.toOption(this.factTable.brand.name),
      ReportBuilderGroups.toOption(this.factTable.count),
    ]),
    ReportBuilderGroups.transaction(this.factTable.transaction),
    ReportBuilderGroups.invoice(this.factTable.invoice),
    ReportBuilderGroups.patient(this.factTable.patient),
  ];

  override readonly availableColumns = [
    ReportBuilderColumns.toColumnGroup('Invoice', [
      ...ReportBuilderColumns.invoice(this.factTable.invoice),
      this.factTable.invoiceLink,
    ]),
    ReportBuilderColumns.toColumnGroup('Patient', [
      ...ReportBuilderColumns.patient(this.factTable.patient),
      this.factTable.patientLink,
    ]),
    ReportBuilderColumns.toColumnGroup('Transaction', [
      ...ReportBuilderColumns.transaction(this.factTable.transaction),
    ]),
  ];

  override readonly defaultColumns = ReportBuilderColumns.toReferences([
    this.factTable.patient.name,
    this.factTable.invoice.status,
    this.factTable.transaction.createdAt,
    this.factTable.transaction.status,
    this.factTable.transaction.type,
    this.factTable.transaction.provider,
    this.factTable.transaction.amount,
  ]);

  override readonly availableCharts = [
    ReportBuilderCharts.predefined(
      'overview.totalPayments',
      this.factTable.transaction.createdAt,
      ChartFilterDefinition.columnOverTime(
        'Total Payments',
        this.factTable.transaction.createdAt,
        [toMeasureBuilderData(this.factTable.transaction.amount)]
      )
    ),
    ReportBuilderCharts.predefined(
      'overview.practice',
      this.factTable.practice.name,
      ChartFilterDefinition.pie('Practice', this.factTable.practice.name, [
        toMeasureBuilderData(this.factTable.transaction.amount),
      ])
    ),
    ...ReportBuilderCommonCharts.patient(
      this.factTable.patient,
      this.factTable.transaction.amount
    ),
    ...ReportBuilderCommonCharts.invoice(
      this.factTable.invoice,
      this.factTable.transaction.amount
    ),
    ReportBuilderCharts.predefined(
      'transaction.type',
      this.factTable.transaction.type,
      ChartFilterDefinition.row('Type', this.factTable.transaction.type, [
        toMeasureBuilderData(this.factTable.transaction.amount),
      ])
    ),
    ReportBuilderCharts.predefined(
      'transaction.status',
      this.factTable.transaction.status,
      ChartFilterDefinition.row('Status', this.factTable.transaction.status, [
        toMeasureBuilderData(this.factTable.transaction.amount),
      ])
    ),
    ReportBuilderCharts.predefined(
      'transaction.provider',
      this.factTable.transaction.provider,
      ChartFilterDefinition.column(
        'Provider',
        this.factTable.transaction.provider,
        [toMeasureBuilderData(this.factTable.transaction.amount)],
        { rotateXAxisLabels: true }
      )
    ),
    ReportBuilderCharts.predefined(
      'transaction.providerSubType',
      this.factTable.transaction.providerSubType,
      ChartFilterDefinition.row(
        'Provider SubType',
        this.factTable.transaction.providerSubType,
        [toMeasureBuilderData(this.factTable.transaction.amount)]
      )
    ),
    ReportBuilderCharts.predefined(
      'transaction.amount',
      this.factTable.transaction.createdAt,
      ChartFilterDefinition.columnOverTime(
        'Amount',
        this.factTable.transaction.createdAt,
        [toMeasureBuilderData(this.factTable.transaction.amount)]
      )
    ),
  ];
}
